"use client";

import * as SheetPrimitive from "@radix-ui/react-dialog";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import type * as React from "react";

import { useDragToClose } from "@/hooks/useDragToClose";
import { cn } from "@/lib/utils";

const DragRegionContext = createContext<
  ((el: HTMLDivElement | null) => void) | null
>(null);

function BottomSheet({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function BottomSheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function BottomSheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function BottomSheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

function BottomSheetContent({
  className,
  children,
  onOpenChange,
  open,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  onOpenChange: (value: boolean) => void;
  open: boolean;
}) {
  const { setDragHandle, style: dragStyle } = useDragToClose({
    onClose: () => onOpenChange?.(false),
    open,
  });

  const dragRegionAttached = useRef(false);

  const setDragRegion = useCallback(
    (el: HTMLDivElement | null) => {
      if (el) dragRegionAttached.current = true;
      setDragHandle(el);
    },
    [setDragHandle],
  );

  // Reset when sheet closes so the dev check fires again on next open
  useEffect(() => {
    if (!open) dragRegionAttached.current = false;
  }, [open]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development" || !open) return;

    // Defer check to allow Portal children to mount and attach refs
    const timer = setTimeout(() => {
      if (!dragRegionAttached.current) {
        throw new Error(
          "BottomSheetContent: No <BottomSheetDragRegion> found. " +
            "Wrap part of the sheet content in <BottomSheetDragRegion> " +
            "to define the swipe-to-close area.",
        );
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);

  return (
    <BottomSheetPortal>
      <BottomSheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom fixed z-50 flex flex-col shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 inset-x-0 bottom-0 h-auto border-t rounded-t-2xl pb-[max(env(safe-area-inset-bottom),0px)]",
          className,
        )}
        {...props}
        style={dragStyle}
      >
        <DragRegionContext.Provider value={setDragRegion}>
          {children}
        </DragRegionContext.Provider>
      </SheetPrimitive.Content>
    </BottomSheetPortal>
  );
}

function BottomSheetDragRegion({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const setDragRegion = useContext(DragRegionContext);

  if (!setDragRegion) {
    throw new Error(
      "BottomSheetDragRegion must be used inside BottomSheetContent.",
    );
  }

  return (
    <div
      ref={setDragRegion}
      className={cn("shrink-0 bg-background pt-4", className)}
      style={{ touchAction: "none" }}
    >
      <div className="flex justify-center pb-2">
        <div className="h-1 w-20 rounded-full bg-muted" aria-hidden="true" />
      </div>
      {children}
    </div>
  );
}

function BottomSheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold text-center", className)}
      {...props}
    />
  );
}

function BottomSheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  BottomSheet,
  BottomSheetContent,
  BottomSheetDescription,
  BottomSheetDragRegion,
  BottomSheetTitle,
  BottomSheetTrigger,
};
